import { useEffect } from "react";

/**
 * Sets --tool-prep-scrollbar-width on an element so
 * padding-right: calc(20px - var(--tool-prep-scrollbar-width)) stays aligned
 * with a non-scrolling 20px-padded sibling.
 */
export function useSyncScrollbarWidth(ref, deps = [], { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref?.current;
    if (!el) return undefined;

    let rafId = 0;

    const applySync = () => {
      rafId = 0;
      const node = ref?.current;
      if (!node) return;
      const width = Math.max(0, node.offsetWidth - node.clientWidth);
      node.style.setProperty("--tool-prep-scrollbar-width", `${width}px`);
    };

    const scheduleSync = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(applySync);
    };

    applySync();

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleSync)
        : null;
    ro?.observe(el);
    el.addEventListener("scroll", scheduleSync, { passive: true });
    window.addEventListener("resize", scheduleSync);

    return () => {
      ro?.disconnect();
      el.removeEventListener("scroll", scheduleSync);
      window.removeEventListener("resize", scheduleSync);
      if (rafId) cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller controls deps
  }, [enabled, ...deps]);
}
